import { useState, type ChangeEvent } from "react";
import { useDateFnsFormattersWithLocale } from "@tellescope/react-hooks";
import { Label, Input, Card, ConfirmDialog } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import type { CalendarEvent } from "@tellescope/types-models";
import type { AppointmentListBlock as AppointmentListBlockType, AppointmentCategory } from "../../../../shared/schema";
import { MobileList } from "../../MobileList";
import { useUsersMap } from "@tellescope/tanstack-query";
import { useCalendarEvents } from "../../../hooks/useCalendarEvents";
import { useAppointmentsFilter } from "../../../hooks/useItemFilter";
import { useCancelCalendarEvent } from "../../../hooks/useCancelCalendarEvent";
import { useJoinVideoCall } from "../../../hooks/useJoinVideoCall";
import { useAppointmentBookingPages } from "../../../hooks/useAppointmentBookingPages";
import { useIframeToken } from "../../../hooks/useIframeToken";
import { AppointmentDetailsDialog } from "../AppointmentDetailsDialog";
import { AppointmentBookingDialog } from "../AppointmentBookingDialog";
import { AppointmentCard } from "./AppointmentCard";
import { AppointmentListSkeleton } from "./AppointmentListSkeleton";
import { CategorySelector } from "./CategorySelector";

interface AppointmentListBlockProps {
  block: AppointmentListBlockType;
}

export const AppointmentListBlock = ({ block }: AppointmentListBlockProps) => {
  const { t } = useTranslation("events");
  const { format } = useDateFnsFormattersWithLocale();
  const formatEventDateTime = (startTimeInMS: number) => format(new Date(startTimeInMS), "EEEE, MMMM d 'at' h:mm a");
  const categories = block.props.categories ?? ["all"];
  const { itemFilter, allowCancel, allowReschedule, rescheduleUrl, rescheduleUrlOverrides, hideVideoAfterMinutes } =
    block.props;
  const showCategorySelector = categories.length > 1;

  // Initialize with the first category in the list
  const [selectedCategory, setSelectedCategory] = useState<AppointmentCategory>(categories[0] ?? "all");

  // State for cancel confirmation dialog
  const [eventToCancel, setEventToCancel] = useState<CalendarEvent | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // State for details dialog
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const [eventToConfirmReschedule, setEventToConfirmReschedule] = useState<CalendarEvent | null>(null);
  const [eventToReschedule, setEventToReschedule] = useState<CalendarEvent | null>(null);

  // Cancel mutation
  const { cancelEventAsync, isPending: isCancelling } = useCancelCalendarEvent();

  // Video calling for Twilio events
  const { joinVideoCall: handleJoinVideoCall } = useJoinVideoCall();

  const {
    bookingPages,
    isLoading: bookingPagesLoading,
    error: bookingPagesError,
  } = useAppointmentBookingPages({ enabled: !!eventToReschedule });
  const rescheduleBookingPage = bookingPages.find((page) => page.id === eventToReschedule?.bookingPageId) ?? null;

  const rescheduleIframeToken = useIframeToken({ enabled: false });

  const {
    events: rawEvents,
    isLoading: eventsLoading,
    error,
  } = useCalendarEvents({
    category: selectedCategory,
  });

  // Apply item filter if configured
  const { filteredItems: events, isLoading: filterLoading } = useAppointmentsFilter({
    items: rawEvents,
    itemFilter,
  });

  // Filter out events hidden from portal
  const visibleEvents = events.filter((e) => !e.hiddenFromPortal);

  // Batch-load host users for events configured to surface the host's personal meeting URL
  const hostIds = Array.from(
    new Set(
      visibleEvents.reduce<string[]>((acc, event) => {
        if (!event.useUserURL) return acc;
        const hostId = event.attendees.find((a) => a.type === "user")?.id;
        if (hostId) acc.push(hostId);
        return acc;
      }, [])
    )
  );
  const hostUsersMap = useUsersMap(hostIds.length > 0 ? hostIds : []);

  const isLoading = eventsLoading || filterLoading;

  const getModalTitle = () => {
    switch (selectedCategory) {
      case "upcoming":
        return t("Upcoming Appointments");
      case "past":
        return t("Past Appointments");
      case "all":
      default:
        return t("All Appointments");
    }
  };

  const getDefaultEmptyMessage = () => {
    switch (selectedCategory) {
      case "upcoming":
        return t("No upcoming appointments");
      case "past":
        return t("No past appointments");
      case "all":
      default:
        return t("No appointments");
    }
  };

  const handleCancelClick = (event: CalendarEvent) => {
    setCancelReason("");
    setEventToCancel(event);
  };

  const handleConfirmCancel = async () => {
    if (!eventToCancel) return;
    await cancelEventAsync({
      eventId: eventToCancel.id,
      cancelReason: cancelReason || undefined,
    });
    setCancelReason("");
    setEventToCancel(null);
  };

  const handleRescheduleClick = (event: CalendarEvent) => {
    setSelectedEvent(null);
    setEventToConfirmReschedule(event);
  };

  const handleConfirmReschedule = async () => {
    if (!eventToConfirmReschedule) return;
    const event = eventToConfirmReschedule;
    setEventToConfirmReschedule(null);
    setEventToReschedule(event);
    try {
      await rescheduleIframeToken.refetch();
    } catch (err) {
      console.error("Failed to get iframe token:", err);
    }
  };

  const handleRescheduleDialogClose = (open: boolean) => {
    if (!open) setEventToReschedule(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <AppointmentListSkeleton count={block.props.maxItems ?? 3} />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="floating" padding="root" className="border-destructive">
        <Card.Body className="p-4 text-center text-destructive">{t("Failed to load appointments")}</Card.Body>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {showCategorySelector && (
        <CategorySelector
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />
      )}
      <MobileList
        items={visibleEvents}
        renderItem={(event) => (
          <AppointmentCard
            event={event}
            allowCancel={allowCancel}
            allowReschedule={allowReschedule}
            rescheduleUrl={rescheduleUrlOverrides?.[event.title] ?? rescheduleUrl}
            hideVideoAfterMinutes={hideVideoAfterMinutes}
            hostUserUrl={
              event.useUserURL ? hostUsersMap[event.attendees.find((a) => a.type === "user")?.id ?? ""]?.url : undefined
            }
            onCancelClick={handleCancelClick}
            onRescheduleClick={handleRescheduleClick}
            onJoinVideoCall={handleJoinVideoCall}
            onViewDetails={setSelectedEvent}
          />
        )}
        title={getModalTitle()}
        maxItems={block.props.maxItems ?? 3}
        emptyMessage={block.props.emptyMessage ?? getDefaultEmptyMessage()}
      />

      {/* Details dialog */}
      <AppointmentDetailsDialog
        event={selectedEvent}
        open={!!selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
        allowCancel={allowCancel ?? false}
        allowReschedule={allowReschedule}
        rescheduleUrl={selectedEvent ? rescheduleUrlOverrides?.[selectedEvent.title] ?? rescheduleUrl : rescheduleUrl}
        hideVideoAfterMinutes={hideVideoAfterMinutes}
        onRescheduleClick={handleRescheduleClick}
      />

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        open={!!eventToCancel}
        onCancel={() => {
          setCancelReason("");
          setEventToCancel(null);
        }}
        variant="destructive"
        title={t("Cancel Appointment")}
        description={
          eventToCancel
            ? t('Are you sure you want to cancel your appointment "{{title}}" scheduled for {{date}}?', {
                title: eventToCancel.displayTitle || eventToCancel.title,
                date: formatEventDateTime(eventToCancel.startTimeInMS),
              })
            : ""
        }
        confirmText={t("Yes, cancel appointment")}
        cancelText={t("Keep appointment")}
        onConfirm={handleConfirmCancel}
        loading={isCancelling}
        confirmDisabled={!!eventToCancel?.requirePortalCancelReason && !cancelReason.trim()}
      >
        <ConfirmDialog.Body className="gap-1">
          <Label htmlFor="cancelReason" required={eventToCancel?.requirePortalCancelReason === true}>
            {t("Reason for cancellation")}
          </Label>
          <Input
            id="cancelReason"
            value={cancelReason}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCancelReason(e.target.value)}
            placeholder={t("Please provide a reason")}
          />
        </ConfirmDialog.Body>
      </ConfirmDialog>

      {/* Reschedule confirmation dialog */}
      <ConfirmDialog
        open={!!eventToConfirmReschedule}
        onCancel={() => setEventToConfirmReschedule(null)}
        title={t("Reschedule Appointment")}
        description={
          eventToConfirmReschedule
            ? t('Are you sure you want to reschedule your appointment "{{title}}" scheduled for {{date}}?', {
                title: eventToConfirmReschedule.displayTitle || eventToConfirmReschedule.title,
                date: formatEventDateTime(eventToConfirmReschedule.startTimeInMS),
              })
            : ""
        }
        confirmText={t("Yes, reschedule")}
        cancelText={t("Keep appointment")}
        onConfirm={handleConfirmReschedule}
      />

      {/* Reschedule booking dialog */}
      <AppointmentBookingDialog
        bookingPage={rescheduleBookingPage}
        open={!!eventToReschedule}
        onOpenChange={handleRescheduleDialogClose}
        iframeToken={rescheduleIframeToken.token}
        isLoading={rescheduleIframeToken.isLoading || bookingPagesLoading}
        error={rescheduleIframeToken.error?.message ?? bookingPagesError?.message ?? null}
        rescheduledCalendarEventId={eventToReschedule?.id}
      />
    </div>
  );
};
